/**
 * Localization service for Russian language support
 * Handles date formatting, number formatting, and text localization
 * Requirements: 12.1, 12.3, 12.5
 */

export type LocaleCode = 'ru' | 'en';

export interface LocalizationOptions {
  locale: LocaleCode;
  dateFormat?: 'short' | 'medium' | 'long';
  timeFormat?: '12h' | '24h';
}

/**
 * Russian localization strings
 */
const RUSSIAN_STRINGS = {
  // Common
  loading: 'Загрузка...',
  error: 'Ошибка',
  success: 'Успешно',
  cancel: 'Отмена',
  save: 'Сохранить',
  delete: 'Удалить',
  edit: 'Редактировать',
  create: 'Создать',
  back: 'Назад',
  next: 'Далее',
  previous: 'Назад',
  confirm: 'Подтвердить',
  
  // Navigation
  home: 'Главная',
  exercises: 'Упражнения',
  programs: 'Программы',
  workout: 'Тренировка',
  history: 'История',
  analytics: 'Аналитика',
  
  // Workout terms
  sets: 'Подходы',
  reps: 'Повторения',
  weight: 'Вес',
  duration: 'Длительность',
  volume: 'Объем',
  rest: 'Отдых',
  
  // Time units
  seconds: 'сек',
  minutes: 'мин',
  hours: 'ч',
  days: 'дн',
  weeks: 'нед',
  months: 'мес',
  
  // Muscle groups
  chest: 'Грудь',
  shoulders: 'Плечи',
  arms: 'Руки',
  legs: 'Ноги',
  core: 'Пресс',
  cardio: 'Кардио',
  
  // Workout status
  notStarted: 'Не начато',
  inProgress: 'В процессе',
  completed: 'Завершено',
  
  // Messages
  workoutCompleted: 'Тренировка завершена!',
  programCreated: 'Программа создана',
  exerciseAdded: 'Упражнение добавлено',
  dataSaved: 'Данные сохранены',
  
  // Errors
  networkError: 'Ошибка сети',
  validationError: 'Ошибка валидации',
  notFound: 'Не найдено',
  unauthorized: 'Нет доступа',
  
  // Placeholders
  searchExercises: 'Поиск упражнений...',
  enterProgramName: 'Введите название программы',
  enterWorkoutName: 'Введите название тренировки',
  addComment: 'Добавить комментарий...',
} as const;

export type TranslationKey = keyof typeof RUSSIAN_STRINGS;

export class LocalizationService {
  private locale: LocaleCode = 'ru';
  private options: LocalizationOptions;

  constructor(options: LocalizationOptions = { locale: 'ru' }) {
    this.locale = options.locale;
    this.options = options;
  }

  /**
   * Get localized string by key
   * Requirements: 12.1
   */
  t(key: TranslationKey): string {
    if (this.locale === 'ru') {
      return RUSSIAN_STRINGS[key];
    }
    
    // Fallback to English (key as string)
    return key;
  }

  /**
   * Format date according to Russian locale
   * Requirements: 12.3
   */
  formatDate(date: Date | string, format: 'short' | 'medium' | 'long' = 'medium'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (this.locale === 'ru') {
      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'Europe/Moscow'
      };

      switch (format) {
        case 'short':
          options.day = '2-digit';
          options.month = '2-digit';
          options.year = '2-digit';
          break;
        case 'medium':
          options.day = 'numeric';
          options.month = 'long';
          options.year = 'numeric';
          break;
        case 'long':
          options.weekday = 'long';
          options.day = 'numeric';
          options.month = 'long';
          options.year = 'numeric';
          break;
      }

      return new Intl.DateTimeFormat('ru-RU', options).format(dateObj);
    }

    return dateObj.toLocaleDateString('en-US');
  }

  /**
   * Format time according to Russian locale
   * Requirements: 12.3
   */
  formatTime(date: Date | string, format: '12h' | '24h' = '24h'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (this.locale === 'ru') {
      const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: format === '12h',
        timeZone: 'Europe/Moscow'
      };

      return new Intl.DateTimeFormat('ru-RU', options).format(dateObj);
    }

    return dateObj.toLocaleTimeString('en-US');
  }

  /**
   * Format datetime according to Russian locale
   * Requirements: 12.3
   */
  formatDateTime(date: Date | string, dateFormat: 'short' | 'medium' | 'long' = 'medium'): string {
    const formattedDate = this.formatDate(date, dateFormat);
    const formattedTime = this.formatTime(date);
    
    return `${formattedDate}, ${formattedTime}`;
  }

  /**
   * Format numbers according to Russian locale
   * Requirements: 12.3
   */
  formatNumber(number: number, options?: Intl.NumberFormatOptions): string {
    if (this.locale === 'ru') {
      return new Intl.NumberFormat('ru-RU', options).format(number);
    }
    
    return new Intl.NumberFormat('en-US', options).format(number);
  }

  /**
   * Format weight with proper units
   * Requirements: 12.3
   */
  formatWeight(weight: number): string {
    const formatted = this.formatNumber(weight, { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 1 
    });
    
    return `${formatted} кг`;
  }

  /**
   * Format duration in minutes to human readable format
   * Requirements: 12.3
   */
  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} ${this.t('minutes')}`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours} ${this.t('hours')}`;
    }
    
    return `${hours} ${this.t('hours')} ${remainingMinutes} ${this.t('minutes')}`;
  }

  /**
   * Format volume (weight × reps) with proper units
   * Requirements: 12.3
   */
  formatVolume(volume: number): string {
    if (volume >= 1000) {
      const volumeInK = volume / 1000;
      return `${this.formatNumber(volumeInK, { maximumFractionDigits: 1 })}к кг`;
    }
    
    return `${this.formatNumber(volume)} кг`;
  }

  /**
   * Pluralize Russian words based on count
   * Requirements: 12.1, 12.5
   */
  pluralize(count: number, forms: [string, string, string]): string {
    const [one, few, many] = forms;
    
    if (this.locale === 'ru') {
      const mod10 = count % 10;
      const mod100 = count % 100;
      
      if (mod100 >= 11 && mod100 <= 19) {
        return many;
      }
      
      if (mod10 === 1) {
        return one;
      }
      
      if (mod10 >= 2 && mod10 <= 4) {
        return few;
      }
      
      return many;
    }
    
    // English fallback
    return count === 1 ? one : few;
  }

  /**
   * Format workout count with proper pluralization
   * Requirements: 12.1, 12.5
   */
  formatWorkoutCount(count: number): string {
    const word = this.pluralize(count, ['тренировка', 'тренировки', 'тренировок']);
    return `${count} ${word}`;
  }

  /**
   * Format exercise count with proper pluralization
   * Requirements: 12.1, 12.5
   */
  formatExerciseCount(count: number): string {
    const word = this.pluralize(count, ['упражнение', 'упражнения', 'упражнений']);
    return `${count} ${word}`;
  }

  /**
   * Format set count with proper pluralization
   * Requirements: 12.1, 12.5
   */
  formatSetCount(count: number): string {
    const word = this.pluralize(count, ['подход', 'подхода', 'подходов']);
    return `${count} ${word}`;
  }

  /**
   * Format relative time (e.g., "2 дня назад")
   * Requirements: 12.3
   */
  formatRelativeTime(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMinutes < 1) {
      return 'только что';
    }
    
    if (diffMinutes < 60) {
      return `${diffMinutes} ${this.pluralize(diffMinutes, ['минуту', 'минуты', 'минут'])} назад`;
    }
    
    if (diffHours < 24) {
      return `${diffHours} ${this.pluralize(diffHours, ['час', 'часа', 'часов'])} назад`;
    }
    
    if (diffDays < 7) {
      return `${diffDays} ${this.pluralize(diffDays, ['день', 'дня', 'дней'])} назад`;
    }
    
    // For older dates, show formatted date
    return this.formatDate(dateObj, 'medium');
  }

  /**
   * Validate Cyrillic text input
   * Requirements: 12.5
   */
  validateCyrillicText(text: string): boolean {
    // Allow Cyrillic letters, spaces, numbers, and common punctuation
    const cyrillicPattern = /^[а-яё\s\d\-.,!?()]*$/i;
    return cyrillicPattern.test(text);
  }

  /**
   * Clean and normalize Cyrillic text
   * Requirements: 12.5
   */
  normalizeCyrillicText(text: string): string {
    return text
      .trim()
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/[ёЁ]/g, (match) => match === 'ё' ? 'е' : 'Е'); // Normalize ё to е
  }

  /**
   * Set locale
   */
  setLocale(locale: LocaleCode): void {
    this.locale = locale;
  }

  /**
   * Get current locale
   */
  getLocale(): LocaleCode {
    return this.locale;
  }
}

// Export singleton instance
export const localizationService = new LocalizationService({ locale: 'ru' });

// Export convenience function
export const t = (key: TranslationKey): string => localizationService.t(key);